import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  getOutboundGroupById,
  getOutboundGroupTrunks,
  updateOutboundGroup,
  applyOutboundGroupRange,
  removeOutboundGroupRange
} from '../../../services';

import PbxContext from '../../../context/PbxContext';
import '../../../libs/bulma.min.css';
import './OutboundCallsForm.css';

import Loading from '../../../components/Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faUsers, faInfoCircle, faArrowRight, faUndo } from '@fortawesome/free-solid-svg-icons';

function OutboundGroupsEdit() {
  const getItensStateGlobal = useContext(PbxContext);
  const { itemsSelectedToEdit } = getItensStateGlobal;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [group, setGroup] = useState(null);
  const [trunks, setTrunks] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [rangeReport, setRangeReport] = useState(null);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const [values, setValues] = useState({
    name: '',
    description: '',
    trunkName: '',
    ispCode: '',
    digitRemove: '',
    active: true
  });

  const [range, setRange] = useState({ first: '', last: '' });

  const history = useHistory();

  const groupId = itemsSelectedToEdit && itemsSelectedToEdit.id ? itemsSelectedToEdit.id : null;

  const loadGroup = useCallback(async () => {
    if (!groupId) return history.push('/config/chamadas/saida/grupos');

    const response = await getOutboundGroupById(groupId);
    if (!response || !response.success || !response.data) {
      setAlert({ show: true, type: 'error', message: (response && response.error) || 'Grupo não encontrado' });
      setLoading(false);
      return;
    }

    const data = response.data;
    setGroup(data);
    setEndpoints(data.endpoints || []);
    setValues({
      name: data.name || '',
      description: data.description || '',
      trunkName: data.trunkName || '',
      ispCode: data.ispCode === null || data.ispCode === undefined ? '' : String(data.ispCode),
      digitRemove: data.digitRemove === null || data.digitRemove === undefined ? '' : String(data.digitRemove),
      active: data.active !== false
    });

    const trunksResponse = await getOutboundGroupTrunks(data.tenant);
    setTrunks(trunksResponse && trunksResponse.success && trunksResponse.data ? trunksResponse.data : []);
    setLoading(false);
  }, [groupId, history]);

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  const handleInputChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const found = {};
    if (!values.name.trim()) found.name = 'Nome é obrigatório';
    if (!values.trunkName) found.trunkName = 'Selecione o tronco de saída do grupo';
    if (values.ispCode && !/^[0-9]{1,10}$/.test(values.ispCode)) found.ispCode = 'Somente dígitos (até 10)';
    if (values.digitRemove !== '' && !(Number.isInteger(Number(values.digitRemove)) && Number(values.digitRemove) >= 0 && Number(values.digitRemove) <= 10)) {
      found.digitRemove = 'Informe um número entre 0 e 10';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    const response = await updateOutboundGroup(group.id, {
      name: values.name.trim(),
      description: values.description.trim() || null,
      trunkName: values.trunkName,
      ispCode: values.ispCode === '' ? null : values.ispCode,
      digitRemove: values.digitRemove === '' ? null : Number(values.digitRemove),
      active: values.active
    });
    setSaving(false);

    if (response && response.success) {
      setAlert({ show: true, type: 'success', message: 'Grupo atualizado e dialplan regenerado.' });
      await loadGroup();
      return;
    }

    setAlert({ show: true, type: 'error', message: (response && response.error) || 'Não foi possível atualizar o grupo' });
  };

  const handleApplyRange = async () => {
    if (!range.first) {
      setAlert({ show: true, type: 'error', message: 'Informe ao menos o ramal inicial' });
      return;
    }

    setApplying(true);
    const response = await applyOutboundGroupRange(group.id, {
      first: Number(range.first),
      last: range.last === '' ? Number(range.first) : Number(range.last)
    });
    setApplying(false);

    if (response && response.success) {
      setRangeReport(response.data);
      setAlert({
        show: true,
        type: 'success',
        message: `${response.data.applied.length} ramal(is) movido(s) para o grupo.`
      });
      await loadGroup();
      return;
    }

    setAlert({ show: true, type: 'error', message: (response && response.error) || 'Não foi possível aplicar a faixa' });
  };

  const handleRemoveRange = async () => {
    setApplying(true);
    const payload = range.first
      ? { first: Number(range.first), last: range.last === '' ? Number(range.first) : Number(range.last) }
      : {};
    const response = await removeOutboundGroupRange(group.id, payload);
    setApplying(false);

    if (response && response.success) {
      setRangeReport(response.data);
      setAlert({
        show: true,
        type: 'success',
        message: `${response.data.applied.length} ramal(is) devolvido(s) ao tronco padrão.`
      });
      await loadGroup();
      return;
    }

    setAlert({ show: true, type: 'error', message: (response && response.error) || 'Não foi possível remover a faixa' });
  };

  const handleCancel = () => history.push('/config/chamadas/saida/grupos');

  const selectedTrunk = trunks.find((trunk) => trunk.trunkName === values.trunkName);

  if (loading) return <Loading />;
  if (!group) {
    return (
      <div className="outbound-form-container">
        <div className="alerts-section">
          <div className="alert-message alert-error">
            <div className="alert-body">{alert.message || 'Grupo não encontrado'}</div>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faTimes} />
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="outbound-form-container">
      <div className="outbound-form-header">
        <div className="outbound-form-title-wrapper">
          <FontAwesomeIcon icon={faUsers} className="outbound-form-icon" />
          <div>
            <h1 className="outbound-form-title">Grupo de Saída: {group.name}</h1>
            <p className="outbound-form-subtitle">
              Identificador <strong>{group.slug}</strong> · Tronco <strong>{group.trunkName}</strong>
              {group.presentedNumber ? ` · Número ${group.presentedNumber}` : ''}
            </p>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faTimes} />
          Voltar
        </button>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Nome do Grupo *</label>
              <input
                className={`form-input ${errors.name ? 'is-danger' : ''}`}
                type="text"
                value={values.name}
                maxLength={100}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
              {errors.name && <p className="form-help is-danger">{errors.name}</p>}
            </div>

            <div className="form-field">
              <label className="form-label">Identificador</label>
              <input className="form-input" type="text" value={group.slug} readOnly disabled />
              <p className="form-help">O identificador faz parte dos contextos do dialplan e não pode ser alterado.</p>
            </div>

            <div className="form-field">
              <label className="form-label">Tronco de Saída *</label>
              <select
                className={`form-select ${errors.trunkName ? 'is-danger' : ''}`}
                value={values.trunkName}
                onChange={(e) => handleInputChange('trunkName', e.target.value)}
              >
                <option value="">Selecione o tronco</option>
                {trunks.map((trunk) => (
                  <option key={trunk.trunkName} value={trunk.trunkName}>
                    {trunk.trunkName}{trunk.fromUser ? ` — ${trunk.fromUser}` : ''}
                  </option>
                ))}
              </select>
              {errors.trunkName && <p className="form-help is-danger">{errors.trunkName}</p>}
              {selectedTrunk && !selectedTrunk.fromUser && (
                <p className="form-help is-warning">
                  Tronco sem número de origem configurado; o número apresentado dependerá da operadora.
                </p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Código Operadora</label>
              <input
                className={`form-input ${errors.ispCode ? 'is-danger' : ''}`}
                type="text"
                value={values.ispCode}
                onChange={(e) => handleInputChange('ispCode', e.target.value)}
                placeholder="Herdado das rotas padrão"
              />
              {errors.ispCode && <p className="form-help is-danger">{errors.ispCode}</p>}
            </div>

            <div className="form-field">
              <label className="form-label">Remove Dígitos</label>
              <input
                className={`form-input ${errors.digitRemove ? 'is-danger' : ''}`}
                type="number"
                min="0"
                max="10"
                value={values.digitRemove}
                onChange={(e) => handleInputChange('digitRemove', e.target.value)}
                placeholder="Herdado das rotas padrão"
              />
              {errors.digitRemove && <p className="form-help is-danger">{errors.digitRemove}</p>}
            </div>

            <div className="form-field">
              <div className="form-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="group-active-checkbox"
                  checked={values.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
                <label htmlFor="group-active-checkbox">Grupo Ativo</label>
              </div>
              <p className="form-help">Só é possível desativar um grupo sem ramais atribuídos.</p>
            </div>

            <div className="form-field form-field-full">
              <label className="form-label">Descrição</label>
              <textarea
                className="form-textarea"
                rows="3"
                value={values.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className={`btn-submit ${saving ? 'is-loading' : ''}`} disabled={saving}>
              <FontAwesomeIcon icon={faSave} />
              Salvar Grupo
            </button>
          </div>
        </div>
      </form>

      <div className="form-section-card">
        <h2 className="outbound-form-title" style={{ fontSize: '1.1rem' }}>Faixa de Ramais</h2>
        <p className="form-help is-info">
          <FontAwesomeIcon icon={faInfoCircle} /> Ao aplicar, cada ramal mantém o seu nível de privilégio atual e passa a
          sair pelo tronco deste grupo. Ramais com contexto fora da escada de privilégios (URA, contextos customizados)
          são ignorados e listados no relatório.
        </p>

        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">Ramal inicial</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={range.first}
              onChange={(e) => setRange((prev) => ({ ...prev, first: e.target.value }))}
              placeholder="Ex.: 1005"
            />
          </div>

          <div className="form-field">
            <label className="form-label">Ramal final</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={range.last}
              onChange={(e) => setRange((prev) => ({ ...prev, last: e.target.value }))}
              placeholder="Ex.: 1009"
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={handleRemoveRange} disabled={applying}>
            <FontAwesomeIcon icon={faUndo} />
            Remover do grupo
          </button>
          <button type="button" className={`btn-submit ${applying ? 'is-loading' : ''}`} onClick={handleApplyRange} disabled={applying}>
            <FontAwesomeIcon icon={faArrowRight} />
            Aplicar faixa ao grupo
          </button>
        </div>

        {rangeReport && (
          <div className="outbound-table-wrapper" style={{ marginTop: '1rem' }}>
            <table className="outbound-table">
              <thead className="outbound-table-header">
                <tr>
                  <th>Ramal</th>
                  <th>De</th>
                  <th>Para</th>
                </tr>
              </thead>
              <tbody>
                {rangeReport.applied.map((item) => (
                  <tr key={`applied-${item.id}`}>
                    <td><strong>{item.id}</strong></td>
                    <td><span className="outbound-badge badge-light">{item.from}</span></td>
                    <td><span className="outbound-badge badge-active">{item.to}</span></td>
                  </tr>
                ))}
                {rangeReport.skipped.map((item) => (
                  <tr key={`skipped-${item.id}`}>
                    <td><strong>{item.id}</strong></td>
                    <td><span className="outbound-badge badge-light">{item.context}</span></td>
                    <td><span className="outbound-badge badge-inactive">Ignorado: {item.reason}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="form-section-card">
        <h2 className="outbound-form-title" style={{ fontSize: '1.1rem' }}>
          Ramais neste grupo ({endpoints.length})
        </h2>
        {endpoints.length === 0 ? (
          <p className="form-help">Nenhum ramal atribuído a este grupo.</p>
        ) : (
          <div className="outbound-table-wrapper">
            <table className="outbound-table">
              <thead className="outbound-table-header">
                <tr>
                  <th>Ramal</th>
                  <th>Contexto</th>
                </tr>
              </thead>
              <tbody>
                {endpoints.map((endpoint) => (
                  <tr key={endpoint.id}>
                    <td><strong>{endpoint.id}</strong></td>
                    <td><span className="outbound-badge badge-mask">{endpoint.context}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default OutboundGroupsEdit;
